import { Skeleton, SkeletonItem, makeStyles, tokens } from '@fluentui/react-components';

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

const useSharedStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
});

// ---------------------------------------------------------------------------
// TableSkeleton
// ---------------------------------------------------------------------------

interface TableSkeletonProps {
  rows?: number;
  columns?: number;
}

const useTableStyles = makeStyles({
  table: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: tokens.spacingVerticalXS,
  },
  headerRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    marginBottom: tokens.spacingVerticalXS,
  },
  bodyRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  cell: {
    flex: 1,
  },
});

export const TableSkeleton: React.FC<TableSkeletonProps> = ({
  rows = 5,
  columns = 4,
}) => {
  const styles = useTableStyles();

  // Vary widths per column so the skeleton looks realistic
  const columnWidths = ['85%', '70%', '60%', '90%', '50%', '75%', '65%', '80%'];

  return (
    <Skeleton aria-label="Loading table data">
      <div className={styles.table}>
        <div className={styles.headerRow}>
          {Array.from({ length: columns }, (_, col) => (
            <div key={col} className={styles.cell}>
              <SkeletonItem size={16} />
            </div>
          ))}
        </div>

        {Array.from({ length: rows }, (_, row) => (
          <div key={row} className={styles.bodyRow}>
            {Array.from({ length: columns }, (_, col) => (
              <div
                key={col}
                className={styles.cell}
                style={{ width: columnWidths[(row + col) % columnWidths.length] }}
              >
                <SkeletonItem size={16} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </Skeleton>
  );
};

// ---------------------------------------------------------------------------
// DetailPageSkeleton
// ---------------------------------------------------------------------------

const useDetailPageStyles = makeStyles({
  titleBar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalL,
  },
  badge: {
    width: '80px',
  },
  tabBar: {
    display: 'flex',
    gap: tokens.spacingHorizontalL,
    marginBottom: tokens.spacingVerticalL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingBottom: tokens.spacingVerticalM,
  },
  tab: {
    width: '72px',
  },
  contentSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  contentRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalL,
  },
  contentLeft: {
    flex: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  contentRight: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
});

export const DetailPageSkeleton: React.FC = () => {
  const styles = useDetailPageStyles();
  const shared = useSharedStyles();

  return (
    <Skeleton aria-label="Loading detail page">
      <div className={shared.container}>
        {/* Title bar */}
        <div className={styles.titleBar}>
          <SkeletonItem shape="rectangle" size={28} style={{ width: '260px' }} />
          <div className={styles.badge}>
            <SkeletonItem shape="rectangle" size={20} />
          </div>
        </div>

        {/* Tab bar */}
        <div className={styles.tabBar}>
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className={styles.tab}>
              <SkeletonItem size={16} />
            </div>
          ))}
        </div>

        {/* Content area */}
        <div className={styles.contentSection}>
          <div className={styles.contentRow}>
            <div className={styles.contentLeft}>
              <SkeletonItem size={16} style={{ width: '90%' }} />
              <SkeletonItem size={16} style={{ width: '75%' }} />
              <SkeletonItem size={16} style={{ width: '60%' }} />
              <SkeletonItem size={16} style={{ width: '85%' }} />
              <SkeletonItem size={16} style={{ width: '50%' }} />
            </div>
            <div className={styles.contentRight}>
              <SkeletonItem shape="rectangle" size={128} />
              <SkeletonItem size={16} style={{ width: '80%' }} />
              <SkeletonItem size={16} style={{ width: '65%' }} />
            </div>
          </div>
        </div>
      </div>
    </Skeleton>
  );
};

// ---------------------------------------------------------------------------
// CardSkeleton
// ---------------------------------------------------------------------------

const useCardStyles = makeStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalL,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  title: {
    width: '60%',
  },
  stat: {
    width: '40%',
  },
  subtitle: {
    width: '80%',
  },
});

export const CardSkeleton: React.FC = () => {
  const styles = useCardStyles();

  return (
    <Skeleton aria-label="Loading card">
      <div className={styles.card}>
        <div className={styles.title}>
          <SkeletonItem size={16} />
        </div>
        <div className={styles.stat}>
          <SkeletonItem size={36} />
        </div>
        <div className={styles.subtitle}>
          <SkeletonItem size={12} />
        </div>
      </div>
    </Skeleton>
  );
};

// ---------------------------------------------------------------------------
// FormSkeleton
// ---------------------------------------------------------------------------

interface FormSkeletonProps {
  fields?: number;
}

const useFormStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    maxWidth: '480px',
    width: '100%',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  label: {
    width: '120px',
  },
});

export const FormSkeleton: React.FC<FormSkeletonProps> = ({ fields = 4 }) => {
  const styles = useFormStyles();

  // Vary input widths so the skeleton feels more natural
  const inputWidths = ['100%', '80%', '100%', '65%', '90%', '70%'];

  return (
    <Skeleton aria-label="Loading form">
      <div className={styles.form}>
        {Array.from({ length: fields }, (_, i) => (
          <div key={i} className={styles.field}>
            <div className={styles.label}>
              <SkeletonItem size={12} />
            </div>
            <SkeletonItem
              size={32}
              shape="rectangle"
              style={{ width: inputWidths[i % inputWidths.length] }}
            />
          </div>
        ))}
      </div>
    </Skeleton>
  );
};
